import { Button, Checkbox, Divider, Group, Paper, ScrollArea, Stack, Text } from '@mantine/core';
import { IconSearch } from '@tabler/icons-react';
import { TextInput } from '@/components';
import type { ReportColumn } from '../types';
import { SelectedColumnsList } from './SelectedColumnsList';

interface Props {
  groupedAvailable: Record<string, ReportColumn[]>;
  selectedKeys: string[];
  selectedColumns: ReportColumn[];
  search: string;
  onSearchChange: (value: string) => void;
  onToggle: (key: string) => void;
  /** Replaces the old onMove — full array, new order. */
  onReorder: (next: ReportColumn[]) => void;
  onSelectAll: () => void;
  onSelectNone: () => void;
  onSelectDefault: () => void;
}

export function ColumnPicker({
  groupedAvailable, selectedKeys, selectedColumns, search, onSearchChange,
  onToggle, onReorder, onSelectAll, onSelectNone, onSelectDefault,
}: Props) {
  return (
    <Paper withBorder p="md" radius="md" h="100%">
      <Group justify="space-between" mb="xs">
        <Text fw={600}>3. Columns</Text>
        <Group gap={4}>
          <Button size="compact-xs" variant="subtle" onClick={onSelectAll}>All</Button>
          <Button size="compact-xs" variant="subtle" color="gray" onClick={onSelectNone}>None</Button>
          <Button size="compact-xs" variant="subtle" color="gray" onClick={onSelectDefault}>Default</Button>
        </Group>
      </Group>

      <TextInput
        placeholder="Search columns..."
        leftSection={<IconSearch size={14} />}
        value={search}
        onChange={(e) => onSearchChange(e.currentTarget.value)}
        mb="sm"
      />

      <Text size="xs" c="dimmed" fw={600} tt="uppercase" mb={4}>Available</Text>
      <ScrollArea.Autosize mah={240} mb="sm">
        <Stack gap="sm">
          {Object.entries(groupedAvailable).map(([category, cols]) => (
            <div key={category}>
              <Text size="xs" c="dimmed" mb={4}>{category}</Text>
              <Stack gap={6}>
                {cols.map((c) => (
                  <Checkbox
                    key={c.key}
                    label={c.label}
                    size="sm"
                    checked={selectedKeys.includes(c.key)}
                    onChange={() => onToggle(c.key)}
                  />
                ))}
              </Stack>
            </div>
          ))}
          {Object.keys(groupedAvailable).length === 0 && (
            <Text size="sm" c="dimmed">No columns match "{search}".</Text>
          )}
        </Stack>
      </ScrollArea.Autosize>

      <Divider my="sm" />

      <SelectedColumnsList
        columns={selectedColumns}
        onReorder={onReorder}
        onRemove={onToggle}
      />
    </Paper>
  );
}